<template>
  <div
    class="camera w-full !h-full flex flex-col aspect-[3/4] md:aspect-video bg-black/60 rounded relative"
  >
    <div
      class="z-10 absolute top-0 start-0 w-full h-full bg-black/60 rounded"
      disable-picture-in-picture
    ></div>
    <video
      id="camera__video-bg"
      class="z-0 absolute top-0 start-0 w-full h-full blur-lg rounded object-cover"
      disable-picture-in-picture
      controlsList="nodownload"
    ></video>
    <canvas id="canvas"></canvas>
    <div
      class="absolute top-0 start-0 w-full h-full z-30 flex flex-col justify-center items-center pt-16"
    >
      <div class="w-full flex flex-col justify-center items-center gap-y-1 text-center">
        <span v-if="ocrStore.ocrType == 1" class="max-w-prose text-xs text-white">
          {{ $t('camPleasePositionEid') }}
        </span>
        <span v-else class="max-w-prose text-xs text-white">
          {{ $t('camPleasePositionPassport') }}
        </span>
        <span class="max-w-[58ch] text-xs text-blue-400">{{ $t('camPleaseEnsure') }}</span>
      </div>
      <div class="w-full h-full p-3 flex justify-center items-center">
        <div
          class="max-w-[24rem] md:max-w-[32rem] w-full aspect-video border-2 border-white rounded-xl relative"
        >
          <video
            ref="videoStream"
            id="camera__video-stream"
            class="rounded-xl w-full h-full object-cover"
            disable-picture-in-picture
            controlsList="nodownload"
          ></video>

          <template v-if="ocrStore.ocrType == 1">
            <div
              v-if="!hasCapture"
              class="p-4 absolute bottom-0 end-0 w-full h-1/3 flex flex-col justify-center items-center gap-y-3 rounded-b-xl border-t border-white"
            >
              <p class="tracking-wide flex flex-wrap gap-2">
                <faChevronLeft
                  :key="i"
                  class="w-[0.35rem] md:w-2.5 fill-white"
                  v-for="i in Array(26 * 3)
                    .fill()
                    .map((_, i) => i)"
                />
              </p>
            </div>
          </template>
          <div
            v-else
            class="absolute top-0 start-0 w-full h-full p-6 rounded-xl flex flex-col justify-end gap-y-6"
          >
            <img :src="ocrPassportPerson" class="w-16 md:w-32 h-auto" />
            <p class="tracking-wide flex flex-wrap gap-2 text-white">
              P<faChevronLeft
                :key="i"
                class="w-[0.3rem] md:w-2.5 fill-white"
                v-for="i in Array(Math.floor(24 * 2.1))
                  .fill()
                  .map((_, i) => i)"
              />
            </p>
          </div>
        </div>
      </div>
      <div class="w-full py-3 flex items-center justify-center"></div>
      <div
        class="w-full px-2 md:px-10 py-3 flex items-center justify-center gap-x-6 bg-black/50 rounded-b"
      >
        <button
          v-if="!hasCapture"
          class="px-1.5 rounded-md bg-gradient-to-tr from-gray-600 to-gray-300 text-white flex items-center hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
          @click.prevent="onCancel"
        >
          <div class="flex justify-center items-center h-full p-2 rounded-s">
            <faTimes class="w-3 fill-white" />
          </div>
          <span class="px-2 ltr:font-sans tracking-wide text-white text-sm pb-[1px]">
            {{ $t('camCancel') }}
          </span>
        </button>
        <div v-if="hasCapture" class="flex gap-x-4 items-center">
          <button
            class="px-1.5 rounded-md border border-white text-white flex items-center hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
            @click.prevent="resumeStream"
          >
            <div class="flex justify-center items-center h-full p-2 rounded-s">
              <ocrCamera class="w-4 fill-white" />
            </div>
            <span class="px-2 ltr:font-sans tracking-wide text-white text-sm pb-[1px]">
              {{ $t('camCaptureAgain') }}
            </span>
          </button>
          <button
            class="px-1.5 rounded-md bg-gradient-to-tr from-linear-s to-linear-e text-white flex items-center hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
            @click.prevent="$emit('onTransition', 'confirm')"
          >
            <div class="flex justify-center items-center h-full p-2 rounded-s">
              <ocrScanIcon class="w-4 fill-white" />
            </div>
            <span class="px-2 ltr:font-sans tracking-wide text-white text-sm pb-[1px]">
              {{ $t('camScanTheInfo') }}
            </span>
          </button>
        </div>
        <button
          v-else
          class="px-1.5 rounded-md bg-gradient-to-br from-linear-s to-linear-e text-white flex items-center hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
          @click.prevent="capturePhoto"
        >
          <div class="flex justify-center items-center h-full p-2 rounded-s">
            <ocrCamera class="w-4 fill-white" />
          </div>
          <span class="px-2 ltr:font-sans tracking-wide text-white text-sm pb-[1px]">
            {{ $t('camCapture') }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import faChevronLeft from '@/assets/icons/faChevronLeft.svg?component'
import faTimes from '@/assets/icons/faTimes.svg?component'
import ocrCamera from '@/assets/icons/ocrCamera.svg?component'
import ocrScanIcon from '@/assets/icons/ocrScan.svg?component'
import ocrPassportPerson from '@/assets/pngs/ocrPassportPerson.png'
import { useOcrStore } from '@/stores/ocr'
import { onMounted, ref } from 'vue'

const emit = defineEmits(['onTransition'])

const ocrStore = useOcrStore()
const hasCapture = ref(false)
const stream = ref()

onMounted(async () => {
  await initializeCameraStream()
})

async function resumeStream() {
  ocrStore.uploadedFile = null
  hasCapture.value = false

  await initializeCameraStream()
}

async function terminateMediaStream() {
  stream.value.getTracks().forEach(function (track) {
    track.stop()
  })
}

async function capturePhoto() {
  const videoBg = document.getElementById('camera__video-bg')
  const video = document.getElementById('camera__video-stream')
  const canvas = document.querySelector('#canvas')
  canvas.width = video.videoWidth * 0.9
  canvas.height = video.videoHeight * 0.9
  canvas.getContext('2d').drawImage(video, 0, 0)

  const imageBase64 = canvas.toDataURL('image/png', 0.6)
  ocrStore.uploadedFile = imageBase64
  ocrStore.operationType = 'capturing'

  hasCapture.value = true
  videoBg.pause()
  video.pause()
  terminateMediaStream()
}

async function initializeCameraStream() {
  const videoBg = document.getElementById('camera__video-bg')
  const video = document.getElementById('camera__video-stream')
  videoBg.disablePictureInPicture = true
  video.disablePictureInPicture = true

  try {
    stream.value = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
    videoBg.srcObject = stream.value
    videoBg.play()
    video.srcObject = stream.value
    video.play()
  } catch (error) {
    emit('onTransition', 'permissionError')
  }
}

async function onCancel() {
  emit('onTransition', 'initial')
  terminateMediaStream()
}
</script>

<style lang="scss" scoped></style>
